/**
 * MCP Browser Intro Tutorial - Beginner
 *
 * A beginner tutorial that introduces MCP (Model Context Protocol) servers
 * through the Browser Automation built-in MCP server. The user creates a
 * 2-step workflow using the Web Browser Agent template.
 */
import { TutorialStep } from '../../types/tutorial';

const MCP_BROWSER_ROUTE_MSG =
  'To continue the tutorial, you need to be in the Workflow Editor.\n\n' +
  'Navigate to __Workflows__ in the sidebar and click on the "Browser Automation" workflow.';

/**
 * Specific values for the MCP browser tutorial.
 */
export const MCP_BROWSER_TUTORIAL_VALUES = {
  orchestration: {
    name: 'Browser Automation',
    description:
      'A 2-step workflow that demonstrates MCP server tools via browser navigation and screenshots.',
  },
};

/**
 * MCP Browser Tutorial Steps
 *
 * A beginner tutorial that introduces MCP (Model Context Protocol) servers
 * through the Browser Automation built-in MCP server.
 * The user creates a 2-step workflow using the Web Browser Agent template.
 *
 * Step Flow:
 * 1-3:   Navigate to Workflows, create workflow
 * 4-6:   Add User Prompt step
 * 7-9:   Add Run Agent step (click +, pick Agent - Create, use template)
 * 10-12: Run, submit input, and view output
 * 13:    Experiment suggestions and wrap-up
 */
export const MCP_BROWSER_TUTORIAL_STEPS: TutorialStep[] = [
  // --- Navigate to Workflows and create ---
  {
    stepNumber: 1,
    targetSelector: '[data-tutorial="nav-workflows"]',
    title: 'Welcome to MCP Servers!',
    description:
      "In this tutorial, you'll learn about __MCP (Model Context Protocol) servers__ — external tools that give agents superpowers.\n\n" +
      'The __Browser Automation__ MCP server provides tools like `browser_navigate`, `browser_screenshot`, and `browser_get_text` that let an agent control a real web browser.\n\n' +
      "You'll build a __2-step workflow__ to see those tools in action!",
    actionPrompt: "Click 'Workflows' in the sidebar to get started.",
    position: 'right',
    completionTrigger: {
      type: 'click',
      value: '[data-tutorial="nav-workflows"]',
    },
    expectedRoute: '/projects/:id',
  },
  {
    stepNumber: 2,
    targetSelector: '[data-tutorial="btn-create-workflow"]',
    title: 'Create the Workflow',
    description:
      "Let's create a new workflow to explore browser automation with MCP tools.",
    actionPrompt: "Click 'New Workflow' to create your workflow.",
    position: 'bottom',
    completionTrigger: {
      type: 'click',
      value: '[data-tutorial="btn-create-workflow"]',
    },
    expectedRoute: '/workflows',
  },
  {
    stepNumber: 3,
    targetSelector: '[data-tutorial="form-workflow"]',
    title: 'Name Your Workflow',
    description:
      'Set up the workflow details:\n\n' +
      '1. For Name, enter: **Browser Automation**\n\n' +
      '2. For Description, enter: **A 2-step workflow that demonstrates MCP server tools via browser navigation and screenshots.**',
    actionPrompt: "Enter the details and click 'Create' to continue.",
    position: 'screen-right',
    hideSpotlight: true,
    completionTrigger: {
      type: 'click',
      value: '[data-tutorial="btn-submit-workflow"]',
    },
    resumeFallbackStep: 2,
  },

  // --- Add User Prompt step ---
  {
    stepNumber: 4,
    targetSelector: '[data-tutorial="btn-add-step"]',
    title: 'Add a User Prompt',
    description:
      'Welcome to the workflow editor! First, we need a way for you to tell the browser agent what to do each time you run.\n\n' +
      'Click the __+__ button below the __Start__ node to add a User Prompt step.',
    actionPrompt: 'Click the __+__ button below the Start node.',
    position: 'screen-right',
    completionTrigger: {
      type: 'click',
      value: '[data-tutorial="btn-add-step"]',
    },
    expectedRoute: '/workflows/:id',
    redirectMessage: MCP_BROWSER_ROUTE_MSG,
  },
  {
    stepNumber: 5,
    targetSelector: '[data-tutorial="step-type-user_prompt"]',
    title: 'Select User Prompt',
    description:
      'Select the __User Prompt__ step type. This will pause the workflow and ask you for instructions before the browser agent runs.',
    actionPrompt: 'Click on __User Prompt__ to select it.',
    position: 'screen-right',
    completionTrigger: {
      type: 'click',
      value: '[data-tutorial="step-option-user_prompt"]',
    },
    expectedRoute: '/workflows/:id',
    redirectMessage: MCP_BROWSER_ROUTE_MSG,
    resumeFallbackStep: 4,
  },
  {
    stepNumber: 6,
    targetSelector: '[data-tutorial="modal-step-config"]',
    title: 'Configure User Prompt',
    description:
      'Configure the User Prompt:\n\n' +
      '__Label__: **Browser Task**\n\n' +
      '__Input Message__: **What would you like the browser agent to do?**',
    actionPrompt: "Fill in the fields, then click 'Add Step'.",
    position: 'screen-right',
    hideSpotlight: true,
    completionTrigger: {
      type: 'click',
      value: '[data-tutorial="btn-submit-step"]',
    },
    expectedRoute: '/workflows/:id',
    redirectMessage: MCP_BROWSER_ROUTE_MSG,
    resumeFallbackStep: 4,
  },

  // --- Add and configure the Run Agent step ---
  {
    stepNumber: 7,
    targetSelector: '[data-tutorial="btn-add-step"]',
    title: 'Add the Browser Agent Step',
    description:
      "Now we'll add a __Run Agent__ step that uses the __Web Browser Agent__ with its MCP tools.\n\n" +
      'The agent has tools like `browser_navigate`, `browser_get_text`, `browser_screenshot`, and `browser_fill` — all provided by the __Browser Automation__ MCP server.',
    actionPrompt: 'Click the __+__ button below the User Prompt step.',
    position: 'screen-right',
    completionTrigger: {
      type: 'click',
      value: '[data-tutorial="btn-add-step"]',
    },
    expectedRoute: '/workflows/:id',
    redirectMessage: MCP_BROWSER_ROUTE_MSG,
  },
  {
    stepNumber: 8,
    targetSelector: '[data-tutorial="step-option-run_agent-create"]',
    title: 'Select Agent - Create',
    description:
      "The Step Type picker has appeared. The __Web Browser Agent__ will __execute__ a one-shot browser task from your prompt, so we'll use the __Create__ intent.",
    actionPrompt: 'Click on __Agent - Create__ to select it.',
    position: 'screen-right',
    completionTrigger: {
      type: 'click',
      value: '[data-tutorial="step-option-run_agent-create"]',
    },
    expectedRoute: '/workflows/:id',
    redirectMessage: MCP_BROWSER_ROUTE_MSG,
    resumeFallbackStep: 7,
  },
  {
    stepNumber: 9,
    targetSelector: '[data-tutorial="modal-step-config"]',
    title: 'Configure the Browser Agent',
    description:
      'The __Run Agent__ step type is already selected. Now set up the agent:\n\n' +
      '1. Click the __+__ button next to the agent dropdown\n' +
      '2. Select __Use Template__, then choose __Web Browser Agent__\n\n' +
      "That's it! The Web Browser Agent comes pre-configured with all the MCP tools it needs. Your prompt from the User Prompt step will tell it what to do.",
    actionPrompt: "Create the agent from template, then click 'Add Step'.",
    position: 'screen-right',
    hideSpotlight: true,
    completionTrigger: {
      type: 'click',
      value: '[data-tutorial="btn-submit-step"]',
    },
    expectedRoute: '/workflows/:id',
    redirectMessage: MCP_BROWSER_ROUTE_MSG,
    resumeFallbackStep: 7,
  },

  // --- Run workflow, submit input, and view results ---
  {
    stepNumber: 10,
    targetSelector: '[data-tutorial="btn-run-workflow"]',
    title: 'Your Workflow is Ready!',
    description:
      'Your workflow has two steps:\n\n' +
      '1. __Browser Task__ — asks you what to do\n' +
      '2. __Browse the Web__ — the Web Browser Agent executes your request using MCP tools\n\n' +
      'The __Browser Automation__ MCP server gives the agent tools like `browser_navigate`, `browser_get_text`, `browser_screenshot`, `browser_fill`, and more — all controlling a real browser.',
    actionPrompt: "Click 'Execute' to run your workflow!",
    position: 'screen-right',
    completionTrigger: {
      type: 'click',
      value: '[data-tutorial="btn-run-workflow"]',
    },
    expectedRoute: '/workflows/:id',
    redirectMessage: MCP_BROWSER_ROUTE_MSG,
  },
  {
    stepNumber: 11,
    targetSelector: '[data-tutorial="btn-submit-user-input"]',
    title: 'Give the Agent a Task',
    description:
      'The workflow is waiting for your input! Type a browser task for the agent.\n\n' +
      'Try one of these:\n' +
      '- **Go to https://news.ycombinator.com and list the top 5 headlines**\n' +
      '- **Go to https://example.com and take a screenshot**\n' +
      '- **Go to https://en.wikipedia.org/wiki/Artificial_intelligence and summarize the first paragraph**',
    actionPrompt: "Enter a task and click 'Submit'.",
    position: 'screen-right',
    hideSpotlight: true,
    completionTrigger: {
      type: 'click',
      value: '[data-tutorial="btn-submit-user-input"]',
    },
    expectedRoute: '/workflows/:id',
    redirectMessage: MCP_BROWSER_ROUTE_MSG,
  },
  {
    stepNumber: 12,
    targetSelector: '[data-tutorial="btn-view-final-output"]',
    title: 'View Your Results!',
    description:
      'Your workflow is running! Watch the step node update as MCP tools are called in real time.\n\n' +
      'Once complete, click on the step to see its output. If you asked for a screenshot, look for the __image displayed below the text output__.\n\n' +
      'The results prove the agent controlled a real browser through MCP tools!',
    actionPrompt:
      'Wait for the workflow to complete, then click on the step to view output.',
    position: 'screen-right',
    hideSpotlight: true,
    completionTrigger: {
      type: 'manual',
      value: '',
    },
    expectedRoute: '/workflows/:id',
    redirectMessage: MCP_BROWSER_ROUTE_MSG,
  },

  // --- Experiment suggestions and wrap-up ---
  {
    stepNumber: 13,
    targetSelector: '[data-tutorial="btn-run-workflow"]',
    title: 'Experiment on Your Own!',
    description:
      "Congratulations! You've seen MCP servers in action. Run the workflow again and try different prompts:\n\n" +
      '__Reading & Extraction__\n' +
      '- **Go to https://news.ycombinator.com and list the top 5 headlines**\n' +
      '- **Go to https://en.wikipedia.org/wiki/Artificial_intelligence and summarize the first paragraph**\n\n' +
      '__Multi-Step Browsing__\n' +
      "- **Go to https://en.wikipedia.org, search for 'Model Context Protocol', and tell me what you find**\n" +
      '- **Go to https://news.ycombinator.com and click on the first story link, then summarize that page**\n\n' +
      '__Form Filling__\n' +
      '- **Go to https://httpbin.org/forms/post and fill in the form with test data, then submit it**\n' +
      "- **Go to https://duckduckgo.com, search for 'browser automation', and tell me the first 3 results**\n\n" +
      '__Screenshots__\n' +
      '- **Go to https://example.com and take a screenshot**\n\n' +
      '__What you learned:__\n' +
      '- What __MCP servers__ are and how they extend agent capabilities\n' +
      '- How the __Browser Automation__ MCP server provides browser control tools\n' +
      '- How a single agent can perform different tasks based on your prompt\n' +
      '- Viewing __screenshot output__ from MCP tool results',
    actionPrompt: "Click 'Finish Tutorial' when you're ready!",
    position: 'screen-right',
    hideSpotlight: true,
    completionTrigger: {
      type: 'manual',
      value: '',
    },
    expectedRoute: '/workflows/:id',
    redirectMessage: MCP_BROWSER_ROUTE_MSG,
  },
];
